import { Icon } from "../icons/icon";
import { ScrollArea } from "../scroll-area/scroll-area";
import { cn } from "../../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

const tableSelectionColumnWidth = 48;
const tableSelectionColumnClassName = "w-12 min-w-12 max-w-12";

type TableColumnLayout = {
  id: string;
  width?: number | string;
  selectionColumn?: boolean;
};

type TableStickyCell = {
  side: "start" | "end";
  offset: number | string;
  boundary: boolean;
};

type TableProps = React.ComponentProps<"table"> & {
  bordered?: boolean;
  columnLayout?: TableColumnLayout[];
  /** Both axes share a viewport; the parent must constrain the table's height. */
  scrollMode?: "horizontal" | "both";
};

type TableSortDirection = false | "asc" | "desc";

type TableHeadProps = React.ComponentProps<"th"> & {
  selectionColumn?: boolean;
  sortDirection?: TableSortDirection;
  sticky?: TableStickyCell;
};

type TableHeadLabelProps = React.ComponentProps<"span">;
type TableHeadTriggerProps = React.ComponentProps<"button">;
type TableResizeHandleProps = Omit<React.ComponentProps<"div">, "width"> & {
  "aria-label": string;
  width: number;
  minWidth: number;
  maxWidth: number;
  resizing?: boolean;
  onResizeBy: (delta: number) => void;
};

type TableCellProps = React.ComponentProps<"td"> & {
  selectionColumn?: boolean;
  sticky?: TableStickyCell;
};

type TableScrollState = {
  canScrollStart: boolean;
  canScrollEnd: boolean;
  hasOverflow: boolean;
};

const TableSectionContext = React.createContext<"header" | "body" | "footer">(
  "body",
);
const TableHeadSortContext = React.createContext<
  TableSortDirection | undefined
>(undefined);

const tableHeadVariants = cva(
  "group group/table-head relative z-0 h-10 px-2 text-start align-middle font-medium whitespace-nowrap text-foreground first:ps-4 last:pe-4 has-[>button[data-table-head-trigger]]:px-0 [&:has([role=checkbox])]:pe-2 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      selectionColumn: {
        true: tableSelectionColumnClassName,
      },
    },
  },
);

const tableCellVariants = cva(
  "group group/table-cell relative z-0 h-[52px] p-2 align-middle whitespace-nowrap first:ps-4 last:pe-4 [&:has([role=checkbox])]:pe-2 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      selectionColumn: {
        true: tableSelectionColumnClassName,
      },
    },
  },
);

function useTableScrollState(
  dir: TableProps["dir"],
  scrollMode: NonNullable<TableProps["scrollMode"]>,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = React.useState<TableScrollState>({
    canScrollStart: false,
    canScrollEnd: false,
    hasOverflow: false,
  });

  React.useEffect(() => {
    const container = containerRef.current;
    const viewport = container?.querySelector<HTMLElement>(
      '[data-slot="scroll-area-viewport"]',
    );
    const table = container?.querySelector<HTMLElement>('[data-slot="table"]');
    if (viewport == null || table == null) return;
    // Pinned-cell shadow casters can retain vertical overflow after rows disappear.
    if (scrollMode === "horizontal") viewport.scrollTop = 0;

    const updateScrollState = () => {
      const tolerance = 1;
      const maxScroll = Math.max(
        0,
        viewport.scrollWidth - viewport.clientWidth,
      );
      const scrollStart = Math.min(maxScroll, Math.abs(viewport.scrollLeft));
      const nextState = {
        hasOverflow: maxScroll > tolerance,
        canScrollStart: scrollStart > tolerance,
        canScrollEnd: scrollStart < maxScroll - tolerance,
      };

      setScrollState((currentState) => {
        if (
          currentState.hasOverflow === nextState.hasOverflow &&
          currentState.canScrollStart === nextState.canScrollStart &&
          currentState.canScrollEnd === nextState.canScrollEnd
        ) {
          return currentState;
        }
        return nextState;
      });
    };

    updateScrollState();
    viewport.addEventListener("scroll", updateScrollState, { passive: true });

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateScrollState);
    resizeObserver?.observe(viewport);
    resizeObserver?.observe(table);

    return () => {
      viewport.removeEventListener("scroll", updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [dir, scrollMode]);

  return { containerRef, scrollState };
}

function Table({
  bordered = false,
  className,
  children,
  columnLayout = [],
  scrollMode = "horizontal",
  dir,
  ...props
}: TableProps) {
  const { containerRef, scrollState } = useTableScrollState(dir, scrollMode);

  return (
    <div
      ref={containerRef}
      dir={dir}
      data-slot="table-container"
      data-horizontal-overflow={scrollState.hasOverflow}
      data-can-scroll-start={scrollState.canScrollStart}
      data-can-scroll-end={scrollState.canScrollEnd}
      className={cn(
        "peer/table-container group/table-container relative w-full",
        scrollMode === "both" &&
          "flex h-full min-h-0 flex-1 flex-col overflow-hidden",
      )}
    >
      <ScrollArea
        dir={dir === "ltr" || dir === "rtl" ? dir : undefined}
        scrollbarOrientation={scrollMode}
        type="auto"
        className={cn(
          "w-full",
          scrollMode === "both" && "min-h-0 flex-1 basis-0",
        )}
        viewportProps={
          scrollMode === "both"
            ? { className: "h-full max-h-full" }
            : undefined
        }
      >
        <div
          data-slot="table-content"
          className={cn("w-full", scrollState.hasOverflow && "pb-2.5")}
        >
          <table
            data-slot="table"
            className={cn(
              "isolate w-full caption-bottom border-separate border-spacing-0 bg-card text-sm [&_tbody>tr:last-child>*]:border-b-0 [&_tfoot>tr:last-child>*]:border-b-0 [&_tfoot>tr>*]:border-t [&_tr>*]:border-b",
              bordered &&
                "[&_tr>*:not(:last-child):not([data-selection-column]):not(:has(+[data-sticky-side=end][data-sticky-boundary=true]))]:border-e [&_tr>[data-sticky-side=end][data-sticky-boundary=true]]:border-s",
              className,
            )}
            {...props}
          >
            {columnLayout.length > 0 ? (
              <colgroup>
                {columnLayout.map((column) => (
                  <col
                    key={column.id}
                    data-selection-column={column.selectionColumn || undefined}
                    style={{
                      width: column.selectionColumn
                        ? tableSelectionColumnWidth
                        : column.width,
                    }}
                  />
                ))}
              </colgroup>
            ) : null}
            {children}
          </table>
        </div>
      </ScrollArea>
    </div>
  );
}

function TableHeader({
  className,
  sticky = false,
  ...props
}: React.ComponentProps<"thead"> & {
  /** Keeps the entire header above body rows in the table's scroll viewport. */
  sticky?: boolean;
}) {
  return (
    <TableSectionContext.Provider value="header">
      <thead
        data-slot="table-header"
        className={cn(
          "[&_tr]:border-b",
          sticky && "sticky top-0 z-30 bg-card",
          className,
        )}
        {...props}
      />
    </TableSectionContext.Provider>
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <TableSectionContext.Provider value="body">
      <tbody
        data-slot="table-body"
        className={cn("[&_tr:last-child]:border-0", className)}
        {...props}
      />
    </TableSectionContext.Provider>
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <TableSectionContext.Provider value="footer">
      <tfoot
        data-slot="table-footer"
        className={cn(
          "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
          className,
        )}
        {...props}
      />
    </TableSectionContext.Provider>
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  const section = React.useContext(TableSectionContext);

  return (
    <tr
      data-slot="table-row"
      className={cn(
        "group/table-row border-b transition-colors data-[state=selected]:bg-muted",
        section === "body" && "hover:bg-muted/50 has-aria-expanded:bg-muted/50",
        className,
      )}
      {...props}
    />
  );
}

function getTableHeadAriaSort(sortDirection: TableSortDirection | undefined) {
  if (sortDirection === "asc") return "ascending";
  if (sortDirection === "desc") return "descending";
  return undefined;
}

function getTableHeadSortIcon(sortDirection: TableSortDirection) {
  if (sortDirection === "asc") return "ArrowUp";
  if (sortDirection === "desc") return "ArrowDown";
  return "ArrowUpDown";
}

function getStickyTableCellClassName(
  sticky: TableStickyCell | undefined,
  section: "header" | "body" | "footer",
) {
  if (sticky == null) return undefined;

  // Extend the shadow caster past each row so its vertical blur cannot restart
  // at cell boundaries; the clipped slice includes the 1px row separator.
  return cn(
    "sticky z-20 transition-[background-color,box-shadow]",
    section === "footer"
      ? "bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]"
      : "bg-card",
    section === "body" &&
      "group-hover/table-row:bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]",
    section === "body" &&
      "group-has-aria-expanded/table-row:bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]",
    "group-data-[state=selected]/table-row:bg-muted",
    sticky.side === "start"
      ? "start-[var(--table-sticky-offset)]"
      : "end-[var(--table-sticky-offset)]",
    sticky.boundary &&
      (sticky.side === "start"
        ? "after:pointer-events-none after:absolute after:inset-x-0 after:-inset-y-8 after:z-10 after:content-[''] after:[clip-path:inset(32px_-20px_31px_100%)] group-data-[can-scroll-start=true]/table-container:after:shadow-lg [&:dir(rtl)]:after:[clip-path:inset(32px_100%_31px_-20px)]"
        : "before:pointer-events-none before:absolute before:inset-x-0 before:-inset-y-8 before:z-10 before:content-[''] before:[clip-path:inset(32px_100%_31px_-20px)] group-data-[can-scroll-end=true]/table-container:before:shadow-lg [&:dir(rtl)]:before:[clip-path:inset(32px_-20px_31px_100%)]"),
  );
}

function getStickyTableCellStyle(
  sticky: TableStickyCell | undefined,
  style: React.CSSProperties | undefined,
) {
  if (sticky == null) return style;

  return {
    ...style,
    "--table-sticky-offset":
      typeof sticky.offset === "number" ? `${sticky.offset}px` : sticky.offset,
  };
}

function TableHead({
  className,
  children,
  selectionColumn = false,
  sortDirection,
  sticky,
  colSpan,
  style,
  ...props
}: TableHeadProps) {
  const ariaSort = getTableHeadAriaSort(sortDirection);
  const section = React.useContext(TableSectionContext);
  const resolvedSticky = colSpan != null && colSpan > 1 ? undefined : sticky;

  return (
    <th
      data-slot="table-head"
      data-selection-column={selectionColumn || undefined}
      data-sticky-side={resolvedSticky?.side}
      data-sticky-boundary={resolvedSticky?.boundary || undefined}
      aria-sort={ariaSort}
      colSpan={colSpan}
      style={getStickyTableCellStyle(resolvedSticky, style)}
      className={cn(
        tableHeadVariants({ selectionColumn }),
        getStickyTableCellClassName(resolvedSticky, section),
        className,
      )}
      {...props}
    >
      <TableHeadSortContext.Provider value={sortDirection}>
        {children}
      </TableHeadSortContext.Provider>
    </th>
  );
}

function TableHeadLabel({
  className,
  children,
  ...props
}: TableHeadLabelProps) {
  const sortDirection = React.useContext(TableHeadSortContext);

  return (
    <span
      data-slot="table-head-label"
      className={cn(
        "inline-flex w-full min-w-0 items-center justify-between gap-2",
        className,
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {sortDirection !== undefined && sortDirection !== false && (
        <Icon
          src={getTableHeadSortIcon(sortDirection)}
          aria-hidden="true"
          className="size-4 shrink-0 text-foreground"
        />
      )}
    </span>
  );
}

function TableHeadTrigger({
  className,
  type = "button",
  ...props
}: TableHeadTriggerProps) {
  return (
    <button
      data-slot="table-head-trigger"
      data-table-head-trigger=""
      type={type}
      className={cn(
        "group/table-head-trigger inline-flex h-full w-full cursor-pointer items-center px-2 [text-align:inherit] align-middle outline-none hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset [th:first-child_&]:ps-4 [th:last-child_&]:pe-4",
        className,
      )}
      {...props}
    />
  );
}

function TableResizeHandle({
  className,
  width,
  minWidth,
  maxWidth,
  resizing = false,
  onResizeBy,
  onKeyDown,
  onMouseDown,
  onTouchStart,
  onClick,
  ...props
}: TableResizeHandleProps) {
  return (
    <div
      data-slot="table-resize-handle"
      data-resizing={resizing || undefined}
      role="separator"
      aria-orientation="vertical"
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuenow={Math.round(width)}
      tabIndex={0}
      className={cn(
        "group/table-resize-handle absolute inset-y-0 -end-[6.5px] z-30 flex w-3 cursor-col-resize touch-none items-center justify-center border-0 outline-none select-none after:h-5 after:w-px after:bg-border after:content-[''] group-hover/table-head:after:bg-foreground hover:after:bg-foreground focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:after:bg-foreground active:after:bg-foreground data-[resizing=true]:after:bg-foreground",
        className,
      )}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        let direction = 0;
        if (event.key === "ArrowRight") direction = 1;
        if (event.key === "ArrowLeft") direction = -1;
        if (direction === 0) return;
        event.preventDefault();
        event.stopPropagation();
        const step = event.shiftKey ? 20 : 10;
        const inlineDirection =
          getComputedStyle(event.currentTarget).direction === "rtl" ? -1 : 1;
        const nextWidth = Math.max(
          minWidth,
          Math.min(maxWidth, width + direction * inlineDirection * step),
        );
        if (nextWidth !== width) onResizeBy(nextWidth - width);
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.blur();
        onMouseDown?.(event);
      }}
      onTouchStart={(event) => {
        event.stopPropagation();
        onTouchStart?.(event);
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      {...props}
    />
  );
}

function TableCellContent({
  children,
  action,
}: {
  children: React.ReactNode;
  action: React.ReactNode;
}) {
  return (
    <div
      data-slot="table-cell-content"
      className="flex min-w-0 items-center gap-2"
    >
      <div className="min-w-0 flex-1">{children}</div>
      <div
        data-slot="table-cell-action"
        className="shrink-0 opacity-0 transition-opacity group-focus-within/table-cell:opacity-100 group-hover/table-cell:opacity-100 [@media(hover:none)]:opacity-100"
      >
        {action}
      </div>
    </div>
  );
}

function TableCell({
  className,
  selectionColumn = false,
  sticky,
  colSpan,
  style,
  ...props
}: TableCellProps) {
  const section = React.useContext(TableSectionContext);
  const resolvedSticky = colSpan != null && colSpan > 1 ? undefined : sticky;

  return (
    <td
      data-slot="table-cell"
      data-selection-column={selectionColumn || undefined}
      data-sticky-side={resolvedSticky?.side}
      data-sticky-boundary={resolvedSticky?.boundary || undefined}
      colSpan={colSpan}
      style={getStickyTableCellStyle(resolvedSticky, style)}
      className={cn(
        tableCellVariants({ selectionColumn }),
        getStickyTableCellClassName(resolvedSticky, section),
        className,
      )}
      {...props}
    />
  );
}

type TableGroupRowProps = Omit<React.ComponentProps<"tr">, "children"> & {
  colSpan: number;
  label: React.ReactNode;
  countLabel?: React.ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  controls?: string;
};

function TableGroupRow({
  className,
  colSpan,
  label,
  countLabel,
  collapsible = false,
  expanded,
  onExpandedChange,
  controls,

  ...props
}: TableGroupRowProps) {
  const isExpanded = expanded ?? false;

  return (
    <tr
      data-slot="table-group-row"
      className={cn("border-b bg-muted/50 text-muted-foreground", className)}
      {...props}
    >
      <td
        data-slot="table-group-cell"
        colSpan={colSpan}
        className="h-auto px-4 py-3 align-middle"
      >
        {collapsible ? (
          <button
            type="button"
            className="flex w-full items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-controls={controls}
            aria-expanded={isExpanded}
            onClick={() => onExpandedChange?.(!isExpanded)}
          >
            <Icon
              src="ChevronDown"
              className={cn(
                "size-4 text-muted-foreground transition-transform",
                isExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
            <TableGroupRowContent label={label} countLabel={countLabel} />
          </button>
        ) : (
          <TableGroupRowContent label={label} countLabel={countLabel} />
        )}
      </td>
    </tr>
  );
}

function TableGroupRowContent({
  label,
  countLabel,
}: Pick<TableGroupRowProps, "label" | "countLabel">) {
  return (
    <div className={cn("flex items-center gap-2")}>
      <div className="font-medium text-muted-foreground">{label}</div>
      {countLabel ? (
        <span className="text-xs text-muted-foreground">{countLabel}</span>
      ) : null}
    </div>
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableHeadLabel,
  TableHeadTrigger,
  TableResizeHandle,
  TableRow,
  TableCell,
  TableCellContent,
  TableGroupRow,
  TableCaption,
  tableSelectionColumnWidth,
};
export type { TableColumnLayout, TableSortDirection, TableStickyCell };
